import React from 'react';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';

interface LaTeXRendererProps {
  children: string;
  displayMode?: boolean;
}

export const LaTeXRenderer: React.FC<LaTeXRendererProps> = ({ children, displayMode = false }) => {
  try {
    if (displayMode) {
      return <BlockMath math={children} />;
    } else {
      return <InlineMath math={children} />;
    }
  } catch (error) {
    // Fallback to plain text if LaTeX parsing fails
    console.warn('LaTeX parsing error:', error);
    return <code className="text-red-500 bg-red-50 px-1 py-0.5 rounded text-xs">LaTeX Error: {children}</code>;
  }
};

// Component to render text with LaTeX support
export const LaTeXText: React.FC<{ children: string }> = ({ children }) => {
  const parts = React.useMemo(() => {
    const result: Array<{ type: 'text' | 'display-math' | 'inline-math'; content: string; key: string }> = [];
    let remaining = children;
    let keyCounter = 0;

    // First, handle display math ($$...$$)
    const displayMathRegex = /\$\$([\s\S]*?)\$\$/g;
    let lastIndex = 0;
    let match;

    while ((match = displayMathRegex.exec(remaining)) !== null) {
      // Add text before the math
      if (match.index > lastIndex) {
        const textBefore = remaining.slice(lastIndex, match.index);
        if (textBefore) {
          result.push({ type: 'text', content: textBefore, key: `text-${keyCounter++}` });
        }
      }

      // Add the display math
      result.push({ 
        type: 'display-math', 
        content: match[1].trim(), 
        key: `display-${keyCounter++}` 
      });

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < remaining.length) {
      const remainingText = remaining.slice(lastIndex);
      if (remainingText) {
        result.push({ type: 'text', content: remainingText, key: `text-${keyCounter++}` });
      }
    }

    // Now process inline math in text parts
    const finalResult: Array<{ type: 'text' | 'display-math' | 'inline-math'; content: string; key: string }> = [];
    
    result.forEach(part => {
      if (part.type === 'text') {
        // Process inline math in this text part
        const inlineMathRegex = /\$([^$\n]+?)\$/g;
        let textRemaining = part.content;
        let textLastIndex = 0;
        let inlineMatch;

        while ((inlineMatch = inlineMathRegex.exec(textRemaining)) !== null) {
          // Add text before the inline math
          if (inlineMatch.index > textLastIndex) {
            const textBefore = textRemaining.slice(textLastIndex, inlineMatch.index);
            if (textBefore) {
              finalResult.push({ type: 'text', content: textBefore, key: `text-${keyCounter++}` });
            }
          }

          // Add the inline math
          finalResult.push({ 
            type: 'inline-math', 
            content: inlineMatch[1].trim(), 
            key: `inline-${keyCounter++}` 
          });

          textLastIndex = inlineMatch.index + inlineMatch[0].length;
        }

        // Add remaining text
        if (textLastIndex < textRemaining.length) {
          const remainingText = textRemaining.slice(textLastIndex);
          if (remainingText) {
            finalResult.push({ type: 'text', content: remainingText, key: `text-${keyCounter++}` });
          }
        }
      } else {
        finalResult.push(part);
      }
    });

    return finalResult;
  }, [children]);

  return (
    <>
      {parts.map(part => {
        switch (part.type) {
          case 'display-math':
            return (
              <div key={part.key} className="my-4 text-center">
                <LaTeXRenderer displayMode={true}>{part.content}</LaTeXRenderer>
              </div>
            );
          case 'inline-math':
            return (
              <LaTeXRenderer key={part.key} displayMode={false}>{part.content}</LaTeXRenderer>
            );
          case 'text':
          default:
            return <span key={part.key}>{part.content}</span>;
        }
      })}
    </>
  );
};